import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import AppEnvironment from '../../components/enums/appEnvironment.enum';
import { getAuthLoginPath } from '../../routes/auth.routes';
import PATHS from '../../routes/routes.const';
import api, { restApi } from '../../services/api';
import { getBrandLoginContext } from '../../services/modules/sso/sso.repository';
import {
  clearFederatedSessionActive,
  getFederatedSessionBrandSlug,
} from '../../services/modules/sso/sso.utils';
import { useAppEnvironmentStore } from '../../stores/AppEnvironment/appEnvironment.store';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../stores/ProductActionsStore/productActions.store';
import useProductDetailsStore from '../../stores/ProductDetails/productDetails.store';
import { TeceoApiResponse } from '../../types/request.types';
import { updateTeamIdInSW } from '../../utils/serviceWorker/utils/serviceWorkerSharedData.util';
import { StorageEnum } from '../context/storage';
import { resetRachelSession } from '../rachel/rachelSession.utils';
import { useAbility } from '../users/AbilityContext';
import { isContractSignPath } from './authContext.utils';
import useStoredSessionSync from './hook/useStoredSessionSync';

export type UserMainStorage = {
  [StorageEnum.SESSION_ID]: string;
  [StorageEnum.TEAM_ID]: string;
  [StorageEnum.ADMIN_TEAM_ID]: string;
  [StorageEnum.BRAND_ID]: string;
  [StorageEnum.APP_ENVIRONMENT]: AppEnvironment;
};

type AuthContextProviderProps = {
  children: React.ReactNode;
};

export type LoginContext = {
  id: string;
  slug: string;
  bannerUrl: string;
  loginImageUrl?: string | null;
  enablePublicShowroom?: boolean;
  enableMarkup?: boolean;
  enableRetailPrice?: boolean;
  brandName: string;
  customerSupportPhone?: string | null;
  hasFederatedLogin?: boolean;
};

export interface AuthContextProps {
  user?: UserMainStorage;
  loggedin: boolean;
  redirectPath: string;
  logout: (options?: { keepLoginContext: boolean }) => Promise<void>;
  handleUnauthorizedSession: () => Promise<void>;
  forceLogout: () => void;

  handleUserLogin: (user: UserMainStorage) => void;
  handleSetLogin: (data: LoginContext) => void;
  loading: boolean;
  loginContext?: LoginContext;
  initContext: () => Promise<void>;
}

export const AuthContext = React.createContext<AuthContextProps>({
  loggedin: false,
  redirectPath: '/',
  user: undefined,
  loading: false,
  handleUserLogin: () => {
    return;
  },
  handleSetLogin: () => {
    return;
  },
  forceLogout: () => {
    return;
  },
  logout: () => Promise.resolve(),
  handleUnauthorizedSession: () => Promise.resolve(),
  loginContext: undefined,
  initContext: () => Promise.resolve(),
});

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const isLoggingOutRef = useRef(false);

  const [user, setUser] = useState<UserMainStorage>();
  const [loggedin, setLoggedin] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/');
  const [loading, setLoading] = useState(true);
  const [loginContext, setLoginContext] = useState<LoginContext>();
  const { resetAppEnvironment } = useAppEnvironmentStore(
    useShallow((state) => ({
      resetAppEnvironment: state.resetAppEnvironment,
    })),
  );

  const resetProductActionsStore = useProductActionsStore(
    (state) => state.resetProductActionsStore,
  );
  const resetFilterWithOrders = useProductDetailsStore(
    (filterState) => filterState.resetFilterWithOrders,
  );
  const resetStore = useOrderBookStore((state) => state.resetStore);

  const { getAbilities } = useAbility();

  const withUser = useCallback(
    async (localUser: UserMainStorage) => {
      api.setToken(localUser[StorageEnum.SESSION_ID]);
      api.setTeam(localUser[StorageEnum.TEAM_ID]);
      api.setAppEnvironment(localUser[StorageEnum.APP_ENVIRONMENT]);

      if (localUser[StorageEnum.ADMIN_TEAM_ID]) {
        api.setAdminTeam(localUser[StorageEnum.ADMIN_TEAM_ID]);
      }

      if (localUser[StorageEnum.BRAND_ID]) {
        api.setBrandContext(localUser[StorageEnum.BRAND_ID]);
      }

      await getAbilities();
    },
    [getAbilities],
  );

  const handleUserLogin = useCallback(
    async (localUser: UserMainStorage) => {
      try {
        await withUser(localUser);
        localStorage.user = JSON.stringify(localUser);
        setUser(localUser);
        setLoggedin(true);
      } catch (_) {
        await logout();
      } finally {
        setLoading(false);
      }
    },
    [withUser],
  );

  const handleSetLogin = (data: LoginContext) => {
    setLoginContext(data);
  };

  const handleUserNotLoggedIn = useCallback(async () => {
    const { pathname, searchParams } = new URL(window.location.href);

    const splittedPathname = pathname.split('/');
    const brandSlug = splittedPathname[1] === 'events' ? splittedPathname[2] : splittedPathname[1];

    const baseUrl = splittedPathname[1] === 'events' ? splittedPathname[1] : 'brands';

    const handleLoginContextError = () => {
      setRedirectPath(searchParams.get('redirectPath') || pathname || '/');
      setLoginContext(undefined);
      setLoading(false);
    };

    if (isContractSignPath({ pathname })) {
      handleLoginContextError();
      return;
    }

    const response: TeceoApiResponse<LoginContext> = await restApi.get(
      `${baseUrl}/${brandSlug}/login-context`,
    );

    if (response.ok) {
      setLoginContext(response.data);
      setRedirectPath(searchParams.get('redirectPath') || `/${response.data?.slug || ''}`);
      setLoading(false);
      return;
    }

    handleLoginContextError();
  }, []);

  const redirectToFederatedBrandLogin = async (brandSlug: string) => {
    setLoading(true);
    history.replace(getAuthLoginPath(brandSlug));

    const response = await getBrandLoginContext(brandSlug);

    if (response.ok) {
      setLoginContext(response.data);
    } else {
      setLoginContext(undefined);
      history.replace(PATHS.public.login.path);
    }

    setLoading(false);
  };

  const endSession = useCallback(async (options?: { keepLoginContext: boolean }) => {
    const federatedBrandSlug = getFederatedSessionBrandSlug();

    api.resetSessionHeaders();
    resetFilterWithOrders();

    queryClient.clear();

    resetProductActionsStore();

    delete localStorage.user;
    setUser(undefined);
    setRedirectPath('/');
    setLoggedin(false);
    resetRachelSession();
    resetAppEnvironment();

    clearFederatedSessionActive();

    const federatedBrandLoginRedirect = federatedBrandSlug
      ? redirectToFederatedBrandLogin(federatedBrandSlug)
      : undefined;

    await api.logout();

    if (federatedBrandLoginRedirect) {
      await federatedBrandLoginRedirect;
    } else if (!options?.keepLoginContext) {
      setLoginContext(undefined);
    }
  }, []);

  const logout = useCallback(
    async (options?: { keepLoginContext: boolean }) => {
      if (isLoggingOutRef.current) {
        return;
      }
      isLoggingOutRef.current = true;

      try {
        await endSession(options);
      } finally {
        isLoggingOutRef.current = false;
      }
    },
    [endSession],
  );

  const { handleUnauthorizedSession } = useStoredSessionSync({
    isLoggedIn: loggedin,
    logout,
  });

  const forceLogout = useCallback(() => {
    const federatedBrandSlug = getFederatedSessionBrandSlug();

    clearFederatedSessionActive();
    api.resetSessionHeaders();
    resetStore();
    resetFilterWithOrders();
    resetRachelSession();
    queryClient.clear();
    delete localStorage.user;
    setUser(undefined);
    setLoggedin(false);

    if (federatedBrandSlug) {
      redirectToFederatedBrandLogin(federatedBrandSlug);
      return;
    }

    history.replace(PATHS.public.login.path);
  }, [resetStore, history]);

  const initContext = useCallback(async () => {
    const stringUser = localStorage.user;
    if (stringUser) {
      await handleUserLogin(JSON.parse(stringUser));
      return;
    }
    await handleUserNotLoggedIn();
  }, [handleUserLogin, handleUserNotLoggedIn]);

  useEffect(() => {
    initContext();
  }, [initContext]);

  useEffect(() => {
    updateTeamIdInSW(user?.[StorageEnum.TEAM_ID] || '');
  }, [user?.[StorageEnum.TEAM_ID]]);

  return (
    <AuthContext.Provider
      value={{
        loggedin,
        redirectPath,
        logout,
        handleUnauthorizedSession,
        user,
        handleUserLogin,
        loading,
        handleSetLogin,
        loginContext,
        initContext,
        forceLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext<AuthContextProps>(AuthContext);
